import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import {DndContext, DragEndEvent, closestCenter} from "@dnd-kit/core";
import {restrictToHorizontalAxis} from "@dnd-kit/modifiers";
import {SortableContext, arrayMove, horizontalListSortingStrategy, useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {Collapse} from "@mui/material";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {RetryRule} from "../../../../apis/requests/payment-flows/types";
import {useSortableSensors} from "../../../../components/sortable";
import {SegmentedToggle} from "./SegmentedToggle";
import {COMBINE_MODE_OPTIONS, RETRY_RULE_LABELS} from "./constants";
import NumberField from "./NumberField";
import {IconLayers} from "../../../../assets";
import {PolicyBlock} from "./PolicyBlock";
import {FormValues} from "./schema";

const DRAG_ACTIVATION_DISTANCE = 4;
const BLOCK_TITLE = "Stop & combine";
const STOP_AFTER_LABEL = "Stop retrying after";
const STOP_AFTER_UNIT = "days from due date";
const COMBINE_LABEL = "When rules overlap";
const PRIORITY_LABEL = "Priority";
const DRAG_HINT = "Drag to reorder";
const RULE_HEIGHT = 30;
const RULE_NUMBER_SIZE = 18;

const SortableRule = ({rule, position}: {rule: RetryRule; position: number}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: rule});
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: RULE_HEIGHT,
        pl: 0.5,
        pr: 1,
        border: `1px solid ${Colors.tertiary._300}`,
        borderRadius: "6px",
        backgroundColor: Colors.bw.white,
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <DragIndicatorIcon sx={{fontSize: 16, color: Colors.grey._400}} />
      <Box
        sx={{
          width: RULE_NUMBER_SIZE,
          height: RULE_NUMBER_SIZE,
          borderRadius: "4px",
          backgroundColor: Colors.tertiary._200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text size={11} variant="inter600" sx={{color: Colors.grey._700}}>
          {position}
        </Text>
      </Box>
      <Text size={13} sx={{color: Colors.grey._900}}>
        {RETRY_RULE_LABELS[rule]}
      </Text>
    </Box>
  );
};

const PolicyBasicsBlock = ({variantIndex}: {variantIndex: number}) => {
  const {control, watch, setValue} = useFormContext<FormValues>();
  const policyPath = `variants.${variantIndex}.policy` as const;
  const combine = watch(`${policyPath}.combine`);
  const order = watch(`${policyPath}.order`);
  const isOrderUsed = combine === "first_match";

  const sensors = useSortableSensors(DRAG_ACTIVATION_DISTANCE);

  const onOrderDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = order.indexOf(active.id as RetryRule);
    const to = order.indexOf(over.id as RetryRule);
    setValue(`${policyPath}.order`, arrayMove(order, from, to), {shouldDirty: true});
  };

  return (
    <PolicyBlock title={BLOCK_TITLE} icon={IconLayers}>
      <Box sx={{display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", gap: 4}}>
        <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
          <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
            {STOP_AFTER_LABEL}
          </Text>
          <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <Controller
              name={`${policyPath}.max_day`}
              control={control}
              render={({field, fieldState}) => (
                <NumberField
                  value={field.value}
                  onChange={field.onChange}
                  ariaLabel="Stop after days"
                  error={!!fieldState.error}
                  suffix={STOP_AFTER_UNIT}
                />
              )}
            />
          </Box>
        </Box>
        <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
          <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
            {COMBINE_LABEL}
          </Text>
          <Box>
            <SegmentedToggle
              ariaLabel="Combine mode"
              value={combine}
              options={COMBINE_MODE_OPTIONS}
              onChange={(next) => setValue(`${policyPath}.combine`, next, {shouldDirty: true})}
            />
          </Box>
        </Box>
      </Box>
      <Collapse in={isOrderUsed} unmountOnExit>
        <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
          <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
            {PRIORITY_LABEL}
          </Text>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={onOrderDragEnd}
          >
            <SortableContext items={order} strategy={horizontalListSortingStrategy}>
              {order.map((rule, index) => (
                <SortableRule key={rule} rule={rule} position={index + 1} />
              ))}
            </SortableContext>
          </DndContext>
          <Text size={12} sx={{color: Colors.grey._500}}>
            {DRAG_HINT}
          </Text>
        </Box>
      </Collapse>
    </PolicyBlock>
  );
};

export default PolicyBasicsBlock;
